import client from './client';
import { SalaryRule } from '../types/entities';
import { ApiResponse, CreateSalaryRuleRequest } from '../types/api';

interface SalaryCalculation {
  driverId: number;
  driverName: string;
  startDate: string;
  endDate: string;
  tripCount: number;
  totalDistanceKm: number;
  calculatedSalary: number;
  trips: Array<{
    tripId: number;
    date: string;
    vehicle: string;
    distanceKm?: number;
    driverWage: number;
  }>;
}

interface MonthlySalary {
  driverId: number;
  driverName: string;
  month: number;
  year: number;
  tripCount: number;
  totalDistanceKm: number;
  totalSalary: number;
}

export const salaryAPI = {
  // Salary Rules
  getRules: async (driverId?: number): Promise<SalaryRule[]> => {
    const response = await client.get('/salaries/rules', {
      params: driverId ? { driverId } : undefined,
    });
    return response.data.data;
  },

  getRuleById: async (id: number): Promise<SalaryRule> => {
    const response = await client.get(`/salaries/rules/${id}`);
    return response.data.data;
  },

  createRule: async (data: CreateSalaryRuleRequest): Promise<SalaryRule> => {
    const response = await client.post('/salaries/rules', data);
    return response.data.data;
  },

  updateRule: async (id: number, data: Partial<CreateSalaryRuleRequest>): Promise<SalaryRule> => {
    const response = await client.put(`/salaries/rules/${id}`, data);
    return response.data.data;
  },

  deleteRule: async (id: number): Promise<ApiResponse<null>> => {
    const response = await client.delete(`/salaries/rules/${id}`);
    return response.data;
  },

  // Salary Calculation
  calculate: async (params: {
    driverId: number;
    startDate: string;
    endDate: string;
  }): Promise<SalaryCalculation> => {
    const response = await client.get('/salaries/calculate', { params });
    return response.data.data;
  },

  getMonthlySalaries: async (month: number, year: number, driverId?: number): Promise<MonthlySalary[]> => {
    const response = await client.get('/salaries/monthly', {
      params: { month, year, driverId },
    });
    // Backend returns { month, year, startDate, endDate, salaries: [...] }
    // We need to extract and transform the salaries array
    const data = response.data.data;
    return (data.salaries || []).map((s: any) => ({
      driverId: s.driverId,
      driverName: s.driverName,
      month: data.month,
      year: data.year,
      tripCount: s.tripCount,
      totalDistanceKm: s.totalDistance,
      totalSalary: s.totalSalary,
    }));
  },

  getDriverSummary: async (
    driverId: number,
    startMonth: number,
    startYear: number,
    endMonth: number,
    endYear: number
  ): Promise<MonthlySalary[]> => {
    const response = await client.get(`/salaries/driver/${driverId}/summary`, {
      params: { startMonth, startYear, endMonth, endYear },
    });
    return response.data.data;
  },
};
